import { useCallback, useMemo } from "react";
import { addDays, startOfDay } from "date-fns";

type DateLike = string | Date;
type CalendarDateInput = string | Date | number;

type UseHotelonlyTravelDatesAvailabilityParams = {
	destinationResort?: string;
	departureDate?: DateLike;
	returnDate?: DateLike;
};

const CALENDAR_MIN_OFFSET_DAYS = 0;
const CALENDAR_DEFAULT_MAX_OFFSET_DAYS = 340;

const RETURN_MIN_AFTER_DEPARTURE_DAYS = 1;
const RETURN_MAX_AFTER_DEPARTURE_DAYS = 30;

export const useHotelonlyTravelDatesAvailability = ({
	destinationResort,
	departureDate,
	returnDate,
}: UseHotelonlyTravelDatesAvailabilityParams) => {
	const today = useMemo(() => startOfDay(new Date()), []);

	const minDate = useMemo(() => addDays(today, CALENDAR_MIN_OFFSET_DAYS).getTime(), [today]);

	const defaultMaxDate = useMemo(
		() => addDays(today, CALENDAR_DEFAULT_MAX_OFFSET_DAYS).getTime(),
		[today]
	);

	const maxReturnDateFromDeparture = useMemo(() => {
		if (!departureDate) {
			return undefined;
		}
		return addDays(
			startOfDay(new Date(departureDate)),
			RETURN_MAX_AFTER_DEPARTURE_DAYS
		).getTime();
	}, [departureDate]);

	const maxDate =
		departureDate && !returnDate && maxReturnDateFromDeparture
			? maxReturnDateFromDeparture
			: defaultMaxDate;

	const isDisabled = !destinationResort;

	const isDateDisabled = useCallback(
		(date?: CalendarDateInput) => {
			if (!date || isDisabled) {
				return true;
			}

			if (departureDate && !returnDate && maxReturnDateFromDeparture) {
				const departureTs = startOfDay(new Date(departureDate)).getTime();
				const dateToCheckTs = startOfDay(new Date(date)).getTime();

				const minReturnDateTs = addDays(
					startOfDay(new Date(departureDate)),
					RETURN_MIN_AFTER_DEPARTURE_DAYS
				).getTime();

				const isValidReturnDate =
					dateToCheckTs >= minReturnDateTs && dateToCheckTs <= maxReturnDateFromDeparture;

				const isValidAnteriorDepartureDate =
					dateToCheckTs >= minDate && dateToCheckTs <= departureTs;

				return !(isValidReturnDate || isValidAnteriorDepartureDate);
			}

			return false;
		},
		[departureDate, returnDate, isDisabled, maxReturnDateFromDeparture, minDate]
	);

	return {
		minDate,
		maxDate,
		isDisabled,
		hasError: false,
		isDateDisabled,
	};
};
